// src/families/global-appearance.ts
var schema = {
  family: "appearance",
  domain: "global",
  contextType: "GLOBAL" /* GLOBAL */,
  version: 1,
  strictMode: false,
  fields: {
    theme: {
      type: "string",
      required: false,
      enum: ["light", "dark", "system"],
      default: "system",
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "UI color theme. 'system' follows the OS preference via prefers-color-scheme."
    },
    density: {
      type: "string",
      required: false,
      enum: ["comfortable", "compact", "spacious"],
      default: "comfortable",
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Content density mode affecting padding, row heights, and component spacing."
    },
    fontSize: {
      type: "number",
      required: false,
      minimum: 12,
      maximum: 24,
      default: 14,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Base font size in pixels. Applied as the root rem reference."
    },
    highContrast: {
      type: "boolean",
      required: false,
      default: false,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Enables WCAG AA high-contrast colour overrides across the UI."
    },
    reducedMotion: {
      type: "boolean",
      required: false,
      default: false,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Suppresses CSS transitions and animations for motion-sensitive users."
    },
    colorBlindMode: {
      type: "string",
      required: false,
      enum: ["none", "protanopia", "deuteranopia", "tritanopia"],
      default: "none",
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Colour vision deficiency correction mode applied via CSS filters."
    },
    sidebarVariant: {
      type: "string",
      required: false,
      enum: ["full", "icons-only", "hidden"],
      default: "full",
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Navigation sidebar display variant."
    }
  }
};
var defaults = {
  theme: "system",
  density: "comfortable",
  fontSize: 14,
  highContrast: false,
  reducedMotion: false,
  colorBlindMode: "none",
  sidebarVariant: "full"
};
var identity = {
  domain: "global",
  contextType: "GLOBAL" /* GLOBAL */,
  contextKey: "default",
  documentFamily: "appearance"
};
var migrations = [];
var globalAppearance = {
  schema,
  defaults,
  migrations
};

// src/families/global-localization.ts
var schemaV1 = {
  family: "localization",
  domain: "global",
  contextType: "GLOBAL" /* GLOBAL */,
  version: 1,
  strictMode: true,
  fields: {
    language: {
      type: "string",
      required: true,
      // BCP 47 pattern: primary-subtag with optional region subtag.
      pattern: "^[a-zA-Z]{2,3}(-[a-zA-Z0-9]{2,8})*$",
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "BCP 47 language tag controlling UI locale and translation namespace selection."
    },
    timezone: {
      type: "string",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "IANA timezone identifier or 'auto' to use the browser-detected timezone."
    },
    dateFormat: {
      type: "string",
      required: true,
      enum: ["iso", "us", "eu", "locale"],
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Date display format applied to all date renderers across the UI."
    },
    timeFormat: {
      type: "string",
      required: true,
      enum: ["12h", "24h"],
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Clock display format (12-hour AM/PM vs 24-hour)."
    },
    numberFormat: {
      type: "string",
      required: true,
      enum: ["period", "comma"],
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Decimal separator convention: 'period' \u2192 1,234.56 | 'comma' \u2192 1.234,56."
    },
    firstDayOfWeek: {
      type: "number",
      required: true,
      enum: [0, 1, 6],
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "First day of the week for calendar components. 0=Sunday, 1=Monday, 6=Saturday."
    }
  }
};
var schemaV2 = {
  ...schemaV1,
  version: 2,
  fields: {
    ...schemaV1.fields,
    measurementSystem: {
      type: "string",
      required: true,
      enum: ["metric", "imperial"],
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Unit system used for speed, distance, and weight display.",
      default: "metric"
    }
  }
};
var schema2 = schemaV2;
var defaults2 = {
  language: "en",
  timezone: "auto",
  dateFormat: "iso",
  timeFormat: "24h",
  numberFormat: "period",
  firstDayOfWeek: 1,
  measurementSystem: "metric"
};
var identity2 = {
  domain: "global",
  contextType: "GLOBAL" /* GLOBAL */,
  contextKey: "default",
  documentFamily: "localization"
};
var migrateV1toV2 = {
  family: "localization",
  fromVersion: 1,
  toVersion: 2,
  migrate(data) {
    return {
      ...data,
      measurementSystem: "metric"
    };
  }
};
var migrations2 = [migrateV1toV2];
var globalLocalization = {
  schema: schema2,
  defaults: defaults2,
  migrations: migrations2
};

// ../theme/dist/tokens.js
var LIGHT_TOKENS = {
  // Signal — mirrors --signal-* in light.css
  signalNominal: "#15803d",
  signalDegraded: "#b45309",
  signalIncident: "#dc2626",
  signalInfo: "#0891b2",
  signalInfoStrong: "#0e6f87",
  signalForecast: "#7c3aed",
  signalNominalBg: "rgba(21,  128, 61,  0.08)",
  signalDegradedBg: "rgba(180, 83,  9,   0.08)",
  signalIncidentBg: "rgba(220, 38,  38,  0.08)",
  signalInfoBg: "rgba(8,   145, 178, 0.08)",
  signalForecastBg: "rgba(124, 58,  237, 0.08)",
  dataSeries: [
    "#2563eb",
    "#7c3aed",
    "#0891b2",
    "#15803d",
    "#b45309",
    "#c2410c",
    "#be185d",
    "#475569"
  ],
  // Surface — mirrors --color-* in light.css
  colorBg: "#f1f5f9",
  colorSurface: "#ffffff",
  colorSurfaceRaised: "#f8fafc",
  colorText: "#0f172a",
  colorTextSecondary: "#475569",
  colorMuted: "#5a6673",
  colorDisabled: "#cbd5e1",
  colorBorder: "#e2e8f0",
  colorBorderStrong: "#cbd5e1",
  // Semantic
  colorSuccess: "#16a34a",
  colorSuccessLight: "rgba(22, 163, 74, 0.08)",
  colorWarning: "#d97706",
  colorWarningLight: "rgba(217, 119, 6, 0.08)",
  colorError: "#dc2626",
  colorErrorLight: "rgba(220, 38, 38, 0.08)",
  // Map vehicle — brighter than signal-nominal for readability on basemap
  mapVehicleOnTime: "#16a34a",
  // brighter map variant; DOM signal uses AA-adjusted #15803d
  mapVehicleEarly: "#3b82f6",
  // blue 500 — "ahead of schedule" (design convention)
  mapVehicleLate: "#d97706",
  // = signalDegraded light
  mapVehicleOutdated: "#64748b",
  // map-specific slate for basemap readability
  mapVehicleOffline: "#94a3b8",
  // slate 400 — lighter than muted
  mapVehicleEmergency: "#dc2626",
  // = signalIncident light
  // Map feature-state — brand-fixed, same in both modes
  mapVehicleSelected: "#2563eb",
  // blue 600
  mapVehicleFocused: "#a855f7",
  // purple 500
  mapDepotSelected: "#00ffff",
  // cyan — Contract v1.1
  // Basemap — lighter feel in light mode
  mapRasterSaturation: -0.15,
  mapRasterBrightnessMin: 0.35,
  mapRasterBrightnessMax: 1,
  mapRasterContrast: 0.1
};
var DARK_TOKENS = {
  // Signal — mirrors --signal-* in dark.css
  signalNominal: "#22c55e",
  signalDegraded: "#f59e0b",
  signalIncident: "#ef4444",
  signalInfo: "#0891b2",
  signalInfoStrong: "#67e8f9",
  signalForecast: "#a78bfa",
  signalNominalBg: "rgba(34,  197, 94,  0.12)",
  signalDegradedBg: "rgba(245, 158, 11,  0.12)",
  signalIncidentBg: "rgba(239, 68,  68,  0.12)",
  signalInfoBg: "rgba(8,   145, 178, 0.18)",
  signalForecastBg: "rgba(167, 139, 250, 0.16)",
  dataSeries: [
    "#60a5fa",
    "#a78bfa",
    "#22d3ee",
    "#4ade80",
    "#fbbf24",
    "#fb923c",
    "#f472b6",
    "#a8a29e"
  ],
  // Surface — mirrors --color-* in dark.css
  colorBg: "#0c0a09",
  colorSurface: "#1c1917",
  colorSurfaceRaised: "#292524",
  colorText: "#f5f5f4",
  colorTextSecondary: "#a8a29e",
  colorMuted: "#9e9892",
  colorDisabled: "#44403c",
  colorBorder: "#292524",
  colorBorderStrong: "#44403c",
  // Semantic
  colorSuccess: "#22c55e",
  colorSuccessLight: "rgba(34, 197, 94, 0.12)",
  colorWarning: "#f59e0b",
  colorWarningLight: "rgba(245, 158, 11, 0.12)",
  colorError: "#ef4444",
  colorErrorLight: "rgba(239, 68, 68, 0.12)",
  // Map vehicle — brighter for dark basemap readability
  mapVehicleOnTime: "#22c55e",
  // = signalNominal dark
  mapVehicleEarly: "#3b82f6",
  // blue 500 — same by convention
  mapVehicleLate: "#f59e0b",
  // = signalDegraded dark
  mapVehicleOutdated: "#78716c",
  // map-specific stone for basemap readability
  mapVehicleOffline: "#9ca3af",
  // gray 400
  mapVehicleEmergency: "#ef4444",
  // = signalIncident dark
  // Map feature-state — brand-fixed, same in both modes
  mapVehicleSelected: "#2563eb",
  mapVehicleFocused: "#a855f7",
  mapDepotSelected: "#00ffff",
  // Basemap — darker, more desaturated for dark mode
  mapRasterSaturation: -0.5,
  mapRasterBrightnessMin: 0.1,
  mapRasterBrightnessMax: 0.85,
  mapRasterContrast: 0.2
};
var SHARED_CANONICAL_COLORS = [
  "#ffffff",
  "#0b0d0f",
  "#ea580c",
  "#2563eb",
  "#e8a030",
  "#5a9eff",
  "#b58cff",
  "#ff7a40",
  "#34c58a",
  "#f5564e",
  "#6ba0f5",
  "#16a371",
  "#f2f3f5"
];
var canonicalColorTokens = {
  light: [
    ...SHARED_CANONICAL_COLORS,
    "#f1f5f9",
    "#ffffff",
    "#f8fafc",
    "#0f172a",
    "#475569",
    "#5a6673",
    "#cbd5e1",
    "#e2e8f0",
    "#c2410c",
    "rgba(234, 88, 12, 0.08)",
    "#16a34a",
    "rgba(22, 163, 74, 0.08)",
    "#d97706",
    "rgba(217, 119, 6, 0.08)",
    "#dc2626",
    "rgba(220, 38, 38, 0.08)",
    "#1c1917",
    "#e7e5e4",
    "#a8a29e",
    "#fdba74",
    "rgba(234, 88, 12, 0.20)",
    "rgba(255, 255, 255, 0.08)",
    "rgba(148, 163, 184, 0.15)",
    "#f5f6f8",
    "#e2e5ea",
    "#ebedf0",
    "rgba(0, 0, 0, 0.02)",
    "rgba(0, 0, 0, 0.04)",
    "#eef1f4",
    "#15803d",
    "#b45309",
    "#0891b2",
    "#7c3aed",
    "rgba(21, 128, 61, 0.08)",
    "rgba(180, 83, 9, 0.08)",
    "rgba(8, 145, 178, 0.08)",
    "rgba(124, 58, 237, 0.08)",
    "#f87171",
    "#991b1b",
    "#92400e",
    "#0e6f87",
    "#fee2e2",
    "#fef3c7",
    "#166534",
    "#d1fae5",
    "#3b82f6",
    "#eff6ff",
    "#dbeafe",
    "#1e293b",
    "#be185d",
    "#1e2028",
    "#eef0f2",
    "rgba(0, 0, 0, 0.04)",
    "rgba(13, 18, 26, 0.35)"
  ],
  dark: [
    ...SHARED_CANONICAL_COLORS,
    "#0c0a09",
    "#1c1917",
    "#292524",
    "#f5f5f4",
    "#a8a29e",
    "#9e9892",
    "#44403c",
    "#fb923c",
    "#f97316",
    "rgba(234, 88, 12, 0.15)",
    "#22c55e",
    "rgba(34, 197, 94, 0.12)",
    "#f59e0b",
    "rgba(245, 158, 11, 0.12)",
    "#ef4444",
    "rgba(239, 68, 68, 0.12)",
    "#d6d3d1",
    "#fdba74",
    "rgba(234, 88, 12, 0.20)",
    "rgba(255, 255, 255, 0.06)",
    "rgba(71, 85, 105, 0.4)",
    "#161412",
    "rgba(255, 255, 255, 0.02)",
    "rgba(255, 255, 255, 0.04)",
    "#3a3633",
    "#0891b2",
    "#a78bfa",
    "rgba(8, 145, 178, 0.18)",
    "rgba(167, 139, 250, 0.16)",
    "#fca5a5",
    "#fbbf24",
    "#67e8f9",
    "rgba(239, 68, 68, 0.08)",
    "rgba(245, 158, 11, 0.08)",
    "#4ade80",
    "rgba(34, 197, 94, 0.08)",
    "#60a5fa",
    "#3b82f6",
    "rgba(96, 165, 250, 0.08)",
    "rgba(96, 165, 250, 0.15)",
    "#e2e8f0",
    "#22d3ee",
    "#f472b6",
    "#0d0f14",
    "#1e242a",
    "#181d22",
    "rgba(255, 255, 255, 0.05)",
    "rgba(0, 0, 0, 0.5)"
  ]
};
function getThemeTokens(theme) {
  return theme === "dark" ? DARK_TOKENS : LIGHT_TOKENS;
}

// src/families/fleet-map-layout.ts
var MAP_TOKENS = getThemeTokens("dark");
var schemaV12 = {
  family: "map-prefs",
  domain: "fleet-visualization",
  contextType: "PAGE" /* PAGE */,
  version: 1,
  strictMode: false,
  fields: {
    showGeofences: { type: "boolean", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    showDepots: { type: "boolean", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    showStops: { type: "boolean", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    showRoutes: { type: "boolean", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    showTraffic: { type: "boolean", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    theme: { type: "string", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    basemapStyle: { type: "string", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    trafficProvider: { type: "string", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    trafficZoom: { type: "number", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    mapSaturation: { type: "number", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    deviationColors: { type: "object", required: false, mergeStrategy: "DEEP_MERGE" /* DEEP_MERGE */ },
    stopColors: { type: "object", required: false, mergeStrategy: "DEEP_MERGE" /* DEEP_MERGE */ },
    clusterRadius: { type: "number", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    labelMinZoom: { type: "number", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    defaultViewport: { type: "object", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    bookmarks: { type: "array", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    lastFilterState: { type: "object", required: false, mergeStrategy: "REPLACE" /* REPLACE */ },
    lineView: { type: "object", required: false, mergeStrategy: "REPLACE" /* REPLACE */ }
  }
};
var schema3 = schemaV12;
var defaults3 = {
  showGeofences: true,
  showDepots: true,
  showStops: true,
  showRoutes: false,
  showTraffic: false,
  trafficProvider: "google",
  trafficZoom: 17,
  mapSaturation: -0.5,
  clusterRadius: 60,
  labelMinZoom: 14,
  deviationColors: {
    ON_TIME: MAP_TOKENS.mapVehicleOnTime,
    EARLY: MAP_TOKENS.mapVehicleEarly,
    LATE: MAP_TOKENS.mapVehicleLate,
    OUTDATED: MAP_TOKENS.mapVehicleOutdated,
    EMERGENCY: MAP_TOKENS.mapVehicleEmergency
  },
  stopColors: {
    BUS: MAP_TOKENS.mapVehicleLate,
    TRAM: MAP_TOKENS.mapVehicleOnTime,
    TRAIN: MAP_TOKENS.mapVehicleEarly
  }
};
var identity3 = {
  domain: "fleet-visualization",
  contextType: "PAGE" /* PAGE */,
  contextKey: "main-map",
  documentFamily: "map-prefs"
};
var migrations3 = [];
var fleetMapLayout = {
  schema: schema3,
  defaults: defaults3,
  migrations: migrations3
};

// src/families/fleet-lineview-state.ts
var schemaV13 = {
  family: "lineview-state",
  domain: "fleet-visualization",
  contextType: "VIEW" /* VIEW */,
  version: 1,
  strictMode: true,
  fields: {
    expandedLineIds: {
      type: "array",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "IDs of line accordion rows currently expanded in the tree.",
      items: { type: "string" }
    },
    timeWindowMode: {
      type: "string",
      required: true,
      enum: ["live", "custom"],
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Whether the timeline tracks live time or a custom offset window."
    },
    startMinutesOffset: {
      type: "number",
      required: true,
      minimum: -480,
      maximum: 0,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Start of the custom time window, as minutes relative to now (must be <= 0)."
    },
    endMinutesOffset: {
      type: "number",
      required: true,
      minimum: 0,
      maximum: 480,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "End of the custom time window, as minutes relative to now (must be >= 0)."
    },
    visibleColumns: {
      type: "array",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Ordered list of column IDs shown in the line view table.",
      items: { type: "string" }
    },
    delayFilterActive: {
      type: "boolean",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "When true, hides service rows that are on-time (below delayThresholdSeconds)."
    },
    delayThresholdSeconds: {
      type: "number",
      required: true,
      minimum: 0,
      maximum: 3600,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Services with delay below this threshold are hidden when delayFilterActive is true."
    }
  }
};
var schema4 = schemaV13;
var defaults4 = {
  expandedLineIds: [],
  timeWindowMode: "live",
  startMinutesOffset: -60,
  endMinutesOffset: 60,
  visibleColumns: ["lineId", "status", "delay", "vehicleId", "driver"],
  delayFilterActive: false,
  delayThresholdSeconds: 120
};
var identity4 = {
  domain: "fleet-visualization",
  contextType: "VIEW" /* VIEW */,
  contextKey: "lineview",
  documentFamily: "lineview-state"
};
var migrations4 = [];
var fleetLineviewState = {
  schema: schema4,
  defaults: defaults4,
  migrations: migrations4
};

// src/families/fleet-chip-config.ts
var schema5 = {
  family: "chip-config",
  domain: "fleet-visualization",
  contextType: "VIEW" /* VIEW */,
  version: 1,
  strictMode: false,
  fields: {
    version: {
      type: "number",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Schema version marker for client-side migration detection."
    },
    chips: {
      type: "object",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Chip visibility and display order.",
      properties: {
        visible: {
          type: "array",
          required: true,
          description: "Chip IDs currently shown.",
          items: { type: "string" }
        },
        order: {
          type: "array",
          required: true,
          description: "Full ordered list of all known chip IDs.",
          items: { type: "string" }
        }
      }
    },
    lastPreset: {
      type: "string",
      required: false,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "ID of the last applied display preset."
    },
    lastRouteId: {
      type: "string",
      required: false,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Route ID of the last viewed route for session restore."
    },
    lastDirectionIndex: {
      type: "number",
      required: false,
      minimum: 0,
      maximum: 1,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Direction index within the last viewed route (0 = outbound, 1 = inbound)."
    }
  }
};
var defaults5 = {
  version: 1,
  chips: {
    visible: [],
    order: []
  }
};
var identity5 = {
  domain: "fleet-visualization",
  contextType: "VIEW" /* VIEW */,
  contextKey: "lineview",
  documentFamily: "chip-config"
};
var migrateV0toV1 = {
  family: "chip-config",
  fromVersion: 0,
  toVersion: 1,
  migrate(data) {
    const chips = data.chips ?? {
      visible: [],
      order: []
    };
    return {
      ...data,
      version: 1,
      chips: {
        visible: chips.visible ?? [],
        order: chips.order ?? []
      }
    };
  }
};
var migrations5 = [migrateV0toV1];
var fleetChipConfig = {
  schema: schema5,
  defaults: defaults5,
  migrations: migrations5
};

// src/families/analytics-dashboard-layout.ts
var schemaV14 = {
  family: "dashboard-layout",
  domain: "analytics",
  contextType: "PAGE" /* PAGE */,
  version: 1,
  strictMode: true,
  fields: {
    gridLayout: {
      type: "array",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "react-grid-layout placement descriptors for each widget on the dashboard."
    },
    enabledWidgetIds: {
      type: "array",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Ordered list of widget IDs currently shown on the dashboard.",
      items: { type: "string" }
    },
    timeRange: {
      type: "string",
      required: true,
      enum: ["last-1h", "last-6h", "last-24h", "last-7d", "last-30d", "custom"],
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Active time range preset for all metrics widgets."
    },
    customStartIso: {
      type: "string",
      required: false,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: 'ISO-8601 UTC start of a custom time range. Only used when timeRange === "custom".'
    },
    customEndIso: {
      type: "string",
      required: false,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: 'ISO-8601 UTC end of a custom time range. Only used when timeRange === "custom".'
    },
    autoRefresh: {
      type: "boolean",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Whether the dashboard polls for new data on a fixed interval."
    },
    refreshIntervalSeconds: {
      type: "number",
      required: true,
      minimum: 30,
      maximum: 3600,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Dashboard auto-refresh cadence in seconds. Minimum 30s to protect server load."
    }
  }
};
var schema6 = schemaV14;
var defaults6 = {
  gridLayout: [],
  enabledWidgetIds: [],
  timeRange: "last-24h",
  customStartIso: null,
  customEndIso: null,
  autoRefresh: true,
  refreshIntervalSeconds: 60
};
var identity6 = {
  domain: "analytics",
  contextType: "PAGE" /* PAGE */,
  contextKey: "default",
  documentFamily: "dashboard-layout"
};
var migrations6 = [];
var analyticsDashboardLayout = {
  schema: schema6,
  defaults: defaults6,
  migrations: migrations6
};

// src/families/dispatch-console-layout.ts
var schemaV15 = {
  family: "console-layout",
  domain: "dispatch",
  contextType: "WORKSPACE" /* WORKSPACE */,
  version: 1,
  strictMode: true,
  fields: {
    panelLayout: {
      type: "string",
      required: true,
      enum: ["single", "split-horizontal", "split-vertical", "quad"],
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Layout mode for the main console work area."
    },
    mapVisible: {
      type: "boolean",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Whether the embedded map panel is rendered in the console."
    },
    composerWidth: {
      type: "number",
      required: true,
      minimum: 280,
      maximum: 800,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Width of the action composer sidebar in pixels."
    },
    eventLogHeight: {
      type: "number",
      required: true,
      minimum: 100,
      maximum: 600,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Height of the bottom event-log panel in pixels."
    },
    eventLogExpanded: {
      type: "boolean",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Whether the event log panel is currently expanded."
    },
    fontSize: {
      type: "number",
      required: true,
      minimum: 11,
      maximum: 18,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Base font size in pixels for console table rows and labels."
    },
    suppressedAlertCategories: {
      type: "array",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Alert category identifiers whose notifications are silenced in this workspace.",
      items: { type: "string" }
    },
    audioAlertsEnabled: {
      type: "boolean",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Whether audio tones play for new critical-severity incidents."
    },
    commandPanelPinned: {
      type: "boolean",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Whether the driver command panel stays open between command actions."
    }
  }
};
var schemaV22 = {
  ...schemaV15,
  version: 2,
  fields: {
    ...schemaV15.fields,
    compactActionBadges: {
      type: "boolean",
      required: true,
      mergeStrategy: "REPLACE" /* REPLACE */,
      description: "Renders action status badges in compact single-character form.",
      default: false
    }
  }
};
var schema7 = schemaV22;
var defaults7 = {
  panelLayout: "split-horizontal",
  mapVisible: true,
  composerWidth: 360,
  eventLogHeight: 200,
  eventLogExpanded: true,
  fontSize: 13,
  suppressedAlertCategories: [],
  audioAlertsEnabled: true,
  commandPanelPinned: false,
  compactActionBadges: false
};
var identity7 = {
  domain: "dispatch",
  contextType: "WORKSPACE" /* WORKSPACE */,
  contextKey: "default",
  documentFamily: "console-layout"
};
var migrateV1toV22 = {
  family: "console-layout",
  fromVersion: 1,
  toVersion: 2,
  migrate(data) {
    return {
      ...data,
      compactActionBadges: false
    };
  }
};
var migrations7 = [migrateV1toV22];
var dispatchConsoleLayout = {
  schema: schema7,
  defaults: defaults7,
  migrations: migrations7
};

// src/families/index.ts
var ALL_FAMILIES = [
  globalAppearance,
  globalLocalization,
  fleetMapLayout,
  fleetLineviewState,
  fleetChipConfig,
  analyticsDashboardLayout,
  dispatchConsoleLayout
];
function registerAllFamilies(schemas, defaults8, migrations8) {
  for (const family of ALL_FAMILIES) {
    schemas.register(family.schema);
    defaults8.register(family.schema.family, family.defaults);
    if (family.migrations) {
      for (const step of family.migrations) {
        migrations8.register(step);
      }
    }
  }
}

export {
  globalAppearance,
  globalLocalization,
  fleetMapLayout,
  fleetLineviewState,
  fleetChipConfig,
  analyticsDashboardLayout,
  dispatchConsoleLayout,
  ALL_FAMILIES,
  registerAllFamilies
};
//# sourceMappingURL=chunk-VSJIN64N.js.map